import { priorityFromChannel } from '../../channelId'
import { PRODUCTS as products } from '../../constants'
import { ageSeconds, max } from '../../util'
import {
    ElementaryStreamTypeName,
    getElementaryStreamType,
    getInputPidsOnInputAppliance,
    getTransportStreamContentInfo,
    getVideoCodecFromPids,
} from './streamHelpers'
import {
    AnyRistServerInputMetric,
    AnyRistServerPortMode,
    Appliance,
    ApplianceConnectionState,
    ApplianceStatus,
    ApplianceType,
    ArrayKeys,
    AsiInputMetrics,
    AsiMetricType,
    AsiOutputMetrics,
    ChannelMetrics,
    CoaxPortMode,
    ComprimatoMetricType,
    ComprimatoPortMode,
    EdgeProduct,
    EntityReference,
    EntityType,
    GeneralEncoderSettings,
    Input,
    InputOperStatus,
    InputPort,
    InputStatus,
    InternalDerivedInputPortMode,
    InternalInputMetrics,
    InternalOutputMetrics,
    IpcPortMode,
    IpInputPort,
    IpPortMode,
    MatroxMetricType,
    MatroxPortMode,
    MatroxSdiOutputPort,
    MetricWindow,
    MptsDemuxMetrics,
    MptsMetricType,
    NmosPortMode,
    Option,
    Output,
    OutputOperStatus,
    OutputPort,
    OutputRedundancyMode,
    OutputStatus,
    Pid,
    PortMode,
    Restrictions,
    RistInputMetrics,
    RistMetricType,
    RistOutputMetrics,
    RistServerEgressMetric,
    RistSimpleProfileInputMetrics,
    RistSimpleProfileMetricType,
    RistSimpleProfileOutputMetrics,
    Role,
    RtmpInputMetrics,
    RtmpMetricType,
    RtmpOutputMetrics,
    RtpInputMetrics,
    RtpOutputMetrics,
    SdiInputMetrics,
    SdiOutputMetrics,
    SrtInputMetrics,
    SrtLiveTransmitInputMetrics,
    SrtLiveTransmitOutputMetrics,
    SrtMetricType,
    SrtOutputMetrics,
    StreamMetrics,
    SupportedVideoCodec,
    TrancodeStreamMetricType,
    TranscodeStreamMetrics,
    UdpInputMetrics,
    UdpOutput1mMetrics,
    UdpOutputMetrics,
    UnixInputMetrics,
    UnixOutput1mMetrics,
    UnixOutputMetrics,
    VideonInputPort,
    VideonPortMode,
    ZixiInputMetrics,
    ZixiMetricType,
    ZixiOutputMetrics,
    AudioLayout,
    AUDIO_LAYOUT_CHANNELS,
    SdiAudioChannelLayoutKey,
    SdiAudioPair,
    sdiAudioChannelSpecs,
} from './types'
import { ErrorCode, InvalidParametersError } from '../../errors'
import { RsInput as ConfigInput, RsOutput as ConfigOutput } from '../../messages'
import { TransportStream, VideoInfo } from '../../tr101Types'
import { ChannelState } from '../../rist'
import { tryStringify } from '../../serialization'
import { isVaApplianceType } from '../../applianceTypeUtil'
import { StreamInfo } from 'common/tr101Types'

export {
    ElementaryStreamTypeName,
    getElementaryStreamType,
    getInputPidsOnInputAppliance,
    getTransportStreamContentInfo,
    getVideoCodecFromPids,
}

/** The SDI channel spec `index` channels into the embedded order, clamped to the last channel. */
export const sdiAudioChannelSpecAt = (index: number): string =>
    sdiAudioChannelSpecs[Math.min(Math.max(index, 0), sdiAudioChannelSpecs.length - 1)]

/**
 * The SDI channel specs (e.g. `['3l','3r']`) a stream occupies, in layout order. Channels missing
 * from the per-channel mapping fall back to consecutive embedded channels from the pair's left.
 */
export const sdiAudioStreamChannelSpecs = (stream: {
    type: AudioLayout
    pair: SdiAudioPair
    channelLayout?: Partial<Record<SdiAudioChannelLayoutKey, string>>
}): string[] => {
    const legacyStart = (stream.pair - 1) * 2
    return AUDIO_LAYOUT_CHANNELS[stream.type].map(
        (key, i) => stream.channelLayout?.[key] ?? sdiAudioChannelSpecAt(legacyStart + i)
    )
}

export const METRICS_FRESHNESS_LIMIT_SECONDS = 30
export const MIN_AUDIO_BITRATE_BPS = 1_000 * 8 // 8 Kbps
export const MIN_VIDEO_BITRATE_BPS = 100_000 // 0.1 Mbps

// Note on value 5.5kbps:
// Inspecting an inhibited/standy RIST-stream in the service overview suggests that ~11kbps is normal bitrate for traffic only so I simply halved that.
// TODO: Ask stream team for an appropriate value
export const MIN_CONTROL_TRAFFIC_BITRATE = 5_500

export function getMinimumAcceptableInputBitrateBps(tsInfo: TransportStream | undefined): number {
    const pids = tsInfo?.pids
    if (!pids) {
        // Not a TS-stream?
        return MIN_VIDEO_BITRATE_BPS
    }
    const { hasMpegTsAudio, hasMpegTsVideo } = getTransportStreamContentInfo(pids)
    if (hasMpegTsAudio && hasMpegTsVideo) {
        return MIN_VIDEO_BITRATE_BPS
    }
    if (hasMpegTsVideo) {
        return MIN_VIDEO_BITRATE_BPS
    }
    if (hasMpegTsAudio) {
        return MIN_AUDIO_BITRATE_BPS
    }
    // No audio or video found in the TS-stream - only private data? We have no idea what is acceptable. Return MIN_VIDEO_BITRATE_BPS for backwards compatibility.
    return MIN_VIDEO_BITRATE_BPS
}

export const isUdpOutputOrUnixOutput1m = (
    ristMetrics: StreamMetrics
): ristMetrics is UdpOutput1mMetrics | UnixOutput1mMetrics =>
    (ristMetrics.type === 'udpOutput' || ristMetrics.type === 'unixOutput') && ristMetrics.window === MetricWindow.m1

export function outputStatus(state: OutputOperStatus, title: string): OutputStatus {
    return {
        state,
        title,
    }
}

export function inputStatus(state: InputOperStatus, title: string): InputStatus {
    return {
        state,
        title,
    }
}

export function getOutputOperState(o: Output): OutputStatus {
    return o.health || { state: OutputOperStatus.metricsMissing, title: 'Health status missing' }
}

export function isAnyRistServerInputMetrics(ristMetrics: StreamMetrics): ristMetrics is AnyRistServerInputMetric {
    return (
        ristMetrics.type === RistMetricType.ristInput ||
        ristMetrics.type === RistMetricType.udpInput ||
        ristMetrics.type === RistMetricType.unixInput ||
        ristMetrics.type === RistMetricType.rtpInput ||
        ristMetrics.type === RistSimpleProfileMetricType.ristSimpleInput ||
        (ristMetrics.type === RistMetricType.srtInput && !isSrtLiveTransmitInputMetrics(ristMetrics)) ||
        ristMetrics.type === RistMetricType.internalInput ||
        ristMetrics.type === RistMetricType.asiInput ||
        ristMetrics.type === RistMetricType.sdiInput
    )
}
export function isRistServerEgressMetrics(
    m: StreamMetrics
): m is RistServerEgressMetric & { isEgress: true; window: MetricWindow.s10 } {
    const isRistServerEgressMetricType =
        m.type === RistMetricType.udpOutput ||
        m.type === RistMetricType.unixOutput ||
        m.type === RistMetricType.rtpOutput ||
        m.type === RistSimpleProfileMetricType.ristSimpleOutput ||
        (m.type === RistMetricType.srtOutput && !isSrtLiveTransmitInputMetrics(m)) ||
        m.type === RistMetricType.internalOutput ||
        m.type === RistMetricType.asiOutput ||
        m.type === RistMetricType.sdiOutput

    return m.window === MetricWindow.s10 && isRistServerEgressMetricType && !!m.isEgress
}

/** @deprecated It can be incorrect for SRT. Use isRistServerEgressMetrics instead. */
export function isRistServerEgressMetricType(
    type: StreamMetrics['type'] | ComprimatoMetricType | MatroxMetricType | AsiMetricType
): type is
    | RistMetricType.udpOutput
    | RistMetricType.unixOutput
    | RistMetricType.rtpOutput
    | RistMetricType.sdiOutput
    | RistSimpleProfileMetricType.ristSimpleOutput {
    const ristServerEgressMetricTypes = [
        RistMetricType.udpOutput,
        RistMetricType.unixOutput,
        RistMetricType.rtpOutput,
        RistMetricType.sdiOutput,
        RistSimpleProfileMetricType.ristSimpleOutput,
    ] as const
    if (isMatroxMetricType(type) || isComprimatoMetricType(type) || isAsiMetricType(type)) {
        return false
    }
    return ristServerEgressMetricTypes.includes(type as (typeof ristServerEgressMetricTypes)[number])
}

function isComprimatoMetricType(
    type: StreamMetrics['type'] | ComprimatoMetricType | MatroxMetricType | AsiMetricType
): type is ComprimatoMetricType {
    return type in ComprimatoMetricType
}
function isMatroxMetricType(
    type: StreamMetrics['type'] | ComprimatoMetricType | MatroxMetricType | AsiMetricType
): type is MatroxMetricType {
    return type in MatroxMetricType
}
function isAsiMetricType(
    type: StreamMetrics['type'] | ComprimatoMetricType | MatroxMetricType | AsiMetricType
): type is AsiMetricType {
    return type in AsiMetricType
}

export function getMetricTypeForOutput(outputPortMode: OutputPort['mode'], applianceType: ApplianceType) {
    if (isVaApplianceType(applianceType)) {
        return RistMetricType.rtpOutput
    }
    switch (concreteStreamingMode(outputPortMode)) {
        case CoaxPortMode.asi:
            return RistMetricType.rtpOutput
        case CoaxPortMode.sdi:
            return RistMetricType.rtpOutput
        case IpPortMode.udp:
            return RistMetricType.udpOutput
        case IpPortMode.rtp:
            return RistMetricType.rtpOutput
        case IpPortMode.rist:
            return RistSimpleProfileMetricType.ristSimpleOutput
        case IpPortMode.srt:
            return SrtMetricType.srtOutput
        case IpPortMode.zixi:
            return ZixiMetricType.zixiOutput
        case IpPortMode.rtmp:
            return RtmpMetricType.rtmpOutput
        case MatroxPortMode.matroxSdi:
            return MatroxMetricType.matroxSdiOutput
        case ComprimatoPortMode.comprimatoNdi:
            return ComprimatoMetricType.comprimatoNdiOutput
        case ComprimatoPortMode.comprimatoSdi:
            return ComprimatoMetricType.comprimatoSdiOutput
        case IpcPortMode.unix:
            return RistMetricType.unixOutput
        case InternalDerivedInputPortMode.internal:
            return RistMetricType.internalInput
    }
}

export function isRistInputMetrics(m: StreamMetrics): m is RistInputMetrics {
    return m.type === RistMetricType.ristInput
}

export function isRistOutputMetrics(m: StreamMetrics): m is RistOutputMetrics {
    return m.type === RistMetricType.ristOutput
}
export function isUnixInputMetrics(m: StreamMetrics): m is UnixInputMetrics {
    return m.type === RistMetricType.unixInput
}

export function isUdpInputMetrics(m: StreamMetrics): m is UdpInputMetrics {
    return m.type === RistMetricType.udpInput
}

export function isInternalInputMetrics(m: StreamMetrics): m is InternalInputMetrics {
    return m.type === RistMetricType.internalInput
}

/**
 * Egress streams that report packet loss. `udpOutput`/`rtpOutput`/`unixOutput` are the handovers to an
 * external process (Zixi, srt-live-transmit, RTMP, Matrox, Comprimato) and behave like any IP output.
 * `internalOutput` is the in-process handover for outputs ristserver serves itself — SDI/ASI hardware and
 * native ristserver SRT — and is the only stream carrying `packetsLost` on those paths, since `sdiOutput`
 * counts frames and `asiOutput` counts bytes.
 */
export function isOutputMetricsWithPacketsLost(
    m: StreamMetrics
): m is UdpOutputMetrics | RtpOutputMetrics | UnixOutputMetrics | InternalOutputMetrics {
    return isUdpOutputMetrics(m) || isRtpOutputMetrics(m) || isUnixOutputMetrics(m) || isInternalOutputMetrics(m)
}

export function isUdpOutputMetrics(m: StreamMetrics): m is UdpOutputMetrics {
    return m.type === RistMetricType.udpOutput
}

export function isInternalOutputMetrics(m: StreamMetrics): m is InternalOutputMetrics {
    return m.type === RistMetricType.internalOutput
}

export function isAsiInputMetrics(m: StreamMetrics): m is AsiInputMetrics {
    return m.type === RistMetricType.asiInput
}

export function isAsiOutputMetrics(m: StreamMetrics): m is AsiOutputMetrics {
    return m.type === RistMetricType.asiOutput
}

export function isSdiInputMetrics(m: StreamMetrics): m is SdiInputMetrics {
    return m.type === RistMetricType.sdiInput
}

export function isSdiOutputMetrics(m: StreamMetrics): m is SdiOutputMetrics {
    return m.type === RistMetricType.sdiOutput
}

export function isUnixOutputMetrics(m: StreamMetrics): m is UnixOutputMetrics {
    return m.type === RistMetricType.unixOutput
}
export function isUdpOutputOrUnixOutputMetrics(m: StreamMetrics): m is UdpOutputMetrics | UnixOutputMetrics {
    return isUdpOutputMetrics(m) || isUnixOutputMetrics(m)
}

export function isRtpInputMetrics(m: StreamMetrics): m is RtpInputMetrics {
    return m.type === RistMetricType.rtpInput
}

export function isMptsMetrics(m: StreamMetrics): m is MptsDemuxMetrics {
    return m.type === MptsMetricType.mptsDemux
}

export function isTranscodeStreamMetrics(m: StreamMetrics): m is TranscodeStreamMetrics {
    return m.type === TrancodeStreamMetricType.transcodeStream
}

export function isRtpOutputMetrics(m: StreamMetrics): m is RtpOutputMetrics {
    return m.type === RistMetricType.rtpOutput
}

export function isRistSimpleProfileInputMetrics(m: StreamMetrics): m is RistSimpleProfileInputMetrics {
    return m.type === RistSimpleProfileMetricType.ristSimpleInput
}

export function isRistSimpleProfileOutputMetrics(m: StreamMetrics): m is RistSimpleProfileOutputMetrics {
    return m.type === RistSimpleProfileMetricType.ristSimpleOutput
}

export function isZixiInputMetrics(m: StreamMetrics): m is ZixiInputMetrics {
    return m.type === ZixiMetricType.zixiInput
}

export function isZixiOutputMetric(m: StreamMetrics): m is ZixiOutputMetrics {
    return m.type === ZixiMetricType.zixiOutput
}

/** @deprecated For backward compat with srt-live-transit */
export function isSrtLiveTransmitInputMetrics(m: StreamMetrics): m is SrtLiveTransmitInputMetrics {
    return m.type === SrtMetricType.srtInput && !('srtConnectionId' in m)
}
/** @deprecated For backward compat with srt-live-transit */
export function isSrtLiveTransmitOutputMetrics(m: StreamMetrics): m is SrtLiveTransmitOutputMetrics {
    return m.type === SrtMetricType.srtOutput && !('srtConnectionId' in m)
}

export function isSrtInputMetric(m: StreamMetrics): m is SrtInputMetrics {
    return m.type === RistMetricType.srtInput || m.type === SrtMetricType.srtInput
}

export function isSrtOutputMetric(m: StreamMetrics): m is SrtOutputMetrics {
    return m.type === RistMetricType.srtOutput || m.type === SrtMetricType.srtOutput
}

export function isRtmpInputMetric(m: StreamMetrics): m is RtmpInputMetrics {
    return m.type === RtmpMetricType.rtmpInput
}

export function isRtmpOutputMetric(m: StreamMetrics): m is RtmpOutputMetrics {
    return m.type === RtmpMetricType.rtmpOutput
}

export function isRistServerChannelMetrics(ristMetrics: StreamMetrics): ristMetrics is ChannelMetrics {
    return ristMetrics.type === RistMetricType.channel
}

/** @deprecated It can be incorrect for SRT. SRT (since R3.25) is not included for the backward compatibility. */
export function isRistServerPortMode(mode?: PortMode): mode is AnyRistServerPortMode {
    return mode === IpPortMode.udp || mode === IpPortMode.rtp || mode === IpPortMode.rist
}

export function isMatroxPortMode(portMode: InputPort['mode']): portMode is MatroxPortMode {
    return portMode in MatroxPortMode
}

export function isGenericVideoAppliancePortMode(portMode: InputPort['mode']): portMode is CoaxPortMode {
    return portMode in CoaxPortMode
}

/** Resolves a selectable meta mode to the protocol that carries the stream. */
export function concreteStreamingMode<T extends PortMode>(mode: T): T extends NmosPortMode ? IpPortMode.rtp : T {
    return (mode === NmosPortMode.nmos ? IpPortMode.rtp : mode) as T extends NmosPortMode ? IpPortMode.rtp : T
}

export function isIpPortMode(portMode: InputPort['mode']): portMode is IpPortMode {
    return portMode in IpPortMode
}

export function isIpPort(inputPort: InputPort): inputPort is IpInputPort {
    return isIpPortMode(inputPort.mode)
}

export function isVideonPortMode(portMode: InputPort['mode'] | OutputPort['mode']): portMode is VideonPortMode {
    return portMode in VideonPortMode
}
// There are currently no Videon output ports
export function isVideonPort(port: InputPort | OutputPort): port is VideonInputPort {
    return isVideonPortMode(port.mode)
}

export function isComprimatoPortMode(portMode: InputPort['mode']): portMode is ComprimatoPortMode {
    return portMode in ComprimatoPortMode
}

export const getInputOperState = (input: Input): InputStatus => {
    return input.health || { state: InputOperStatus.metricsMissing, title: 'Health status missing' }
}

export function getActiveChannelOnAppliance(applianceId: string, ristMetrics: StreamMetrics[]): number | undefined {
    return (
        ristMetrics.find(
            (m) =>
                m.type === RistMetricType.channel && m.applianceId === applianceId && m.state === ChannelState.activated
        )?.channelId ??
        // Backwards compatibility for appliances that don't have "channel.state" introduced in R3.15.0: Use first found channelId
        ristMetrics.find((m) => m.type === RistMetricType.channel && m.applianceId === applianceId)?.channelId
    )
}

export const getTransferredString = (gbValue?: number) => {
    if (gbValue === undefined) {
        return
    }

    let measure = 'GB'
    let value: number | string = gbValue
    if (Math.floor(gbValue) > 0) {
        value = gbValue.toFixed(2)
    } else {
        measure = 'MB'
        value = (gbValue * 1e3).toFixed(2)
        if (parseFloat(value) === 0) {
            value = `~${value}`
        }
    }
    return `${value} ${measure}`
}

export const validatePassword = (value: string): string | undefined => {
    const minPwdLength = 8
    if (value.length > 0) {
        if (value.length < minPwdLength) {
            return `Password must contain at least ${minPwdLength} characters`
        }
    }
    return undefined
}

/**
 * Email regex imported from Zod library.
 * @see https://github.com/colinhacks/zod/blob/main/packages/docs/content/api.mdx
 */
export const REGEX_EMAIL = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i

export const REGEX_ALPHANUMERIC = /^[a-zA-Z0-9]*$/m

export const validateEmail = (value: string): string | undefined => {
    if (!REGEX_EMAIL.test(value)) {
        return 'is not a valid email'
    }
    return undefined
}

export const MAX_USERNAME_LENGTH = 254

export const validateUsername = (value: string): string | undefined => {
    if (!REGEX_EMAIL.test(value)) {
        return `Must be a valid email address`
    }
    return undefined
}

export const validate = (fieldname: string, validationErrorInfo: string | undefined) => {
    if (validationErrorInfo) {
        throw new InvalidParametersError(ErrorCode.invalidParameters, [
            { name: fieldname, reason: validationErrorInfo },
        ])
    }
}

export const getEntityTypeName = (entityType: EntityType): string => {
    switch (entityType) {
        case EntityType.outputRecipientList:
            return 'output list'
        case EntityType.groupRecipientList:
            return 'group list'
        case EntityType.inputList:
            return 'input list'
        default:
            return entityType
    }
}

export function notUndefined<T>(x: T | undefined): x is T {
    return x !== undefined
}

export function notUndefinedOrNull<T>(x: T | undefined | null): x is T {
    return x !== undefined && x !== null
}

// Pick the tsInfo entry that describes the input itself. For derived inputs the backend
// prepends the parent's entry, so `tsInfo[0]` is unsafe (EDGE-6645). Returns undefined
// when no entry matches — callers must treat that as "format unknown", not fall back to [0].
export function tsInfoForChannels(
    tsInfo: TransportStream[] | undefined,
    channelIds: number[] | undefined
): TransportStream | undefined {
    if (!tsInfo || !channelIds) return undefined
    const channelIdsByPriority = [...channelIds].sort((a, b) => priorityFromChannel(a) - priorityFromChannel(b))
    for (const channelId of channelIdsByPriority) {
        const match = tsInfo.find((ts) => ts.channelId === channelId)
        if (match) return match
    }
    return undefined
}

export function getFormattedTransportStreamContent(tsInfo: TransportStream | undefined): string {
    if (!tsInfo) {
        return 'N/A'
    }
    const pids = tsInfo.pids
    if (!pids) {
        return 'No pids'
    }
    const streamIsMpts = isMpts([tsInfo])
    if (streamIsMpts) {
        return 'MPTS'
    }

    const { hasMpegTsAudio, hasMpegTsVideo } = getTransportStreamContentInfo(pids)
    if (hasMpegTsVideo) {
        return videoFormatFromTransportStream(pids)
    } else if (hasMpegTsAudio) {
        return audioFormatFromTransportStream(pids)
    }
    return `No audio/video`
}

export function tsInfoServiceName(info?: TransportStream) {
    const firstService = info?.services?.[0]
    const noInfo = 'no info'
    if (!firstService) {
        return noInfo
    }
    return `${firstService.name || firstService.type?.description || noInfo}`
}

export function audioFormatFromTransportStream(tsPids: (Pid | undefined)[]): string {
    // TODO: Handle MPTS - we currently extract the first found audio entry
    const audioPid = tsPids.find((p) => p?.streamInfo?.audio)
    if (!audioPid) {
        return 'No audio'
    }
    const audio = audioPid.streamInfo!.audio!
    const sampleRate = audio.sampleRate ? `${audio.sampleRate / 1000}kHz ` : ''
    const codec = getFormattedElementaryStreamType(audioPid)
    return `${sampleRate}${codec}`
}

export function formattedVideoHeight({
    height,
    isHEVC,
    isInterlaced,
}: {
    height: number
    isHEVC: boolean
    isInterlaced: boolean
}) {
    const multiplier = isInterlaced && isHEVC ? 2 : 1
    return (height * multiplier).toString()
}

export function formattedVideoRate(framerate: number | undefined):
    | {
          frameRate: string
          fieldRate: string
      }
    | undefined {
    if (framerate === undefined) return undefined

    const fieldrate = framerate * 2
    return {
        frameRate: framerate.toFixed(2),
        fieldRate: fieldrate.toFixed(2),
    }
}

/** Pixel format (chroma subsampling and bit depth) of a video stream, e.g. "4:2:2 10bit". Undefined when not reported. */
export const formatPixelFormat = (video?: VideoInfo): string | undefined => {
    if (!video?.chromaFormat) return
    let pixelFormat = video.chromaFormat.description
    const primaryBitDepth = video.bitDepthLuma ?? video.bitDepthChroma
    if (primaryBitDepth) {
        pixelFormat += ` ${primaryBitDepth}bit`
    }
    return pixelFormat
}

export function videoFormatFromTransportStream(tsPids: (Pid | undefined)[]): string {
    // TODO: Handle MPTS - we currently extract the first found video entry
    const videoPid = tsPids.find(
        (
            p
        ): p is Pid & {
            streamInfo: StreamInfo & { video: VideoInfo }
        } => !!p?.streamInfo?.video
    )
    if (!videoPid) {
        return 'No video'
    }
    const streamInfo = videoPid.streamInfo
    const isHEVC = getElementaryStreamType(streamInfo.type?.value) === ElementaryStreamTypeName.h265
    const isInterlaced = streamInfo.video.interlaced === 'yes'

    const videoHeight =
        streamInfo.video.videoSize?.vertical !== undefined
            ? formattedVideoHeight({
                  height: streamInfo.video.videoSize.vertical,
                  isHEVC,
                  isInterlaced,
              })
            : ''
    const scanMode = streamInfo.video.interlaced ? (isInterlaced ? 'i' : 'p') : '?'
    const videoRate = formattedVideoRate(streamInfo.video.frameRate)
    const frameOrFieldrate = videoRate ? (isInterlaced ? videoRate.fieldRate : videoRate.frameRate) : ''
    const formattedCodec = getFormattedElementaryStreamType(videoPid)
    const pixelFormat = formatPixelFormat(streamInfo.video)
    return `${videoHeight}${scanMode}${frameOrFieldrate} ${formattedCodec}${pixelFormat ? ` ${pixelFormat}` : ''}`
}

export const isOutput = (inputOrOutput: ConfigInput | ConfigOutput): inputOrOutput is ConfigOutput => {
    return (
        inputOrOutput.type === 'push-rist-output' ||
        inputOrOutput.type === 'rist-simple-output' ||
        inputOrOutput.type === 'srt-output' ||
        inputOrOutput.type === 'zixi-output' ||
        inputOrOutput.type === 'udp-output' ||
        inputOrOutput.type === 'rtp-output' ||
        inputOrOutput.type === 'rtmp-output'
    )
}

/**
 * Format bitrate into kbps, Mbps, Gbps, Tbps depending on the size.
 * @param bps - bitrate in bps
 */
export function formatBitrate(bps?: number | null | string) {
    if (typeof bps === 'string') {
        // oxlint-disable-next-line no-param-reassign
        bps = parseInt(bps)
    }
    if (typeof bps !== 'number' || !Number.isFinite(bps)) {
        return 'N/A'
    }
    const units = [
        ['kbps', 0],
        ['Mbps', 2],
        ['Gbps', 3],
        ['Tbps', 3],
    ] as const
    const absBps = Math.max(bps, 0)
    const log = Math.floor(Math.log10(absBps))
    const size = Math.max(Math.floor(log / 3) - 1, 0)
    const [unit, decimals] = units[size]
    const val = absBps / Math.pow(10, (size + 1) * 3)
    const formattedVal = val.toFixed(decimals)
    return `${formattedVal} ${unit}`
}

/**
 * Format file size into TB, GB, MB, KB depending on the size.
 * @param bytes - file size in bytes
 */
export const formatFileSize = (bytes?: number | null | string, fallback: string = 'N/A') => {
    if (typeof bytes === 'string') {
        // oxlint-disable-next-line no-param-reassign
        bytes = parseInt(bytes)
    }

    if (typeof bytes !== 'number' || !Number.isFinite(bytes)) {
        return fallback
    }

    const units = [
        ['B', 0],
        ['KB', 0],
        ['MB', 1],
        ['GB', 2],
        ['TB', 3],
    ] as const

    const absBytes = Math.max(bytes, 0)
    const log = Math.floor(Math.log10(absBytes))
    const size = Math.max(Math.floor(log / 3), 0)
    const [unit, decimals] = units[size]
    const val = absBytes / Math.pow(10, size * 3)
    const formattedVal = val.toFixed(decimals)
    return `${formattedVal} ${unit}`
}

/**
 * Format milliseconds into human-friendly string like "1m40s", "2h3m", or "500ms"
 * @param ms - milliseconds
 */
export const formatMs = (ms?: number | null | string): string => {
    if (typeof ms === 'string') {
        ms = parseInt(ms)
    }

    if (typeof ms !== 'number' || !Number.isFinite(ms)) {
        return 'N/A'
    }

    const isNegative = ms < 0
    ms = Math.abs(ms)

    const totalSeconds = Math.floor(ms / 1000)
    const milliseconds = ms % 1000
    const seconds = totalSeconds % 60
    const totalMinutes = Math.floor(totalSeconds / 60)
    const minutes = totalMinutes % 60
    const hours = Math.floor(totalMinutes / 60)

    let result: string
    if (hours > 0) {
        result = `${hours}h${minutes}m`
    } else if (minutes > 0) {
        result = `${minutes}m${seconds}s`
    } else if (seconds > 0) {
        result = `${seconds}s`
    } else {
        result = `${milliseconds}ms`
    }

    return isNegative ? `-${result}` : result
}

export interface SentenceCaseOptions {
    /** Treat hyphens as word delimiters (default: false) */
    hyphenDelims?: boolean
}

/**
 * Converts camelCase, PascalCase, or an arbitrary sentence to sentence case.
 * Optionally treats hyphens as word delimiters.
 *
 * @example
 * sentenceCase('myVariableName') // 'My variable name'
 * sentenceCase('A qUiCk bRoWn-foX') // 'A quick brown-fox'
 * sentenceCase('some-hyphenated-string', { hyphenDelims: true }) // 'Some hyphenated string'
 */
export const sentenceCase = (input: string, options?: SentenceCaseOptions): string => {
    const dehyphenated = options?.hyphenDelims ? input.replace(/-/g, ' ') : input
    const spaced = dehyphenated.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    const normalized = spaced.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    const lowerCased = normalized.toLowerCase()
    return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1)
}

export const APPLIANCE_ONLINE_THRESHOLD_SEC = 15
export function getApplianceStatus(
    appliance: Pick<Appliance, 'lastMessageAt'>,
    formatDistanceToNow: (date: Date) => string
): ApplianceStatus {
    if (!appliance.lastMessageAt) {
        return {
            title: `Never connected`,
            state: ApplianceConnectionState.neverConnected,
        }
    }
    if (ageSeconds(appliance.lastMessageAt) > APPLIANCE_ONLINE_THRESHOLD_SEC) {
        return {
            title: `Last seen ${formatDistanceToNow(appliance.lastMessageAt)} ago`,
            state: ApplianceConnectionState.missing,
        }
    }
    return {
        title: `Last seen less than ${APPLIANCE_ONLINE_THRESHOLD_SEC} seconds ago`,
        state: ApplianceConnectionState.connected,
    }
}

export function formatGraphNodeId({ type, id }: EntityReference) {
    return `${type}::${id}`
}

export function splitGraphNodeId(nodeId: string): EntityReference {
    const [type, id] = nodeId.split('::')
    return { type, id } as EntityReference
}

export function isMpts(tsInfo?: Input['tsInfo']) {
    return tsInfo?.some((tsInfo) => (tsInfo.services?.length || 1) > 1)
}

export function getVideoCodec(tsInfo: TransportStream): ElementaryStreamTypeName | null {
    return getVideoCodecFromPids(tsInfo.pids || [])
}

export function getAudioCodec(tsInfo: TransportStream): ElementaryStreamTypeName | null {
    const audioPid = tsInfo.pids?.find((p) => p?.streamInfo?.audio !== undefined)
    if (!audioPid) {
        return null
    }
    return getElementaryStreamType(audioPid.streamInfo?.type?.value)
}

function getFormattedElementaryStreamType(pid: Pid): string {
    const streamTypeValue = pid.streamInfo?.type?.value
    if (streamTypeValue) {
        const mappedStreamType = getElementaryStreamType(streamTypeValue)
        if (mappedStreamType !== null) {
            return mappedStreamType.toString()
        }
    }

    const pidDescription = pid.description
    if (pidDescription) {
        return pidDescription
    }

    if (streamTypeValue) {
        return `(${streamTypeValue})`
    }

    return '?'
}

/**
 * https://en.wikipedia.org/wiki/Program-specific_information#Elementary_stream_types
 */
// return start of month in UTC
export const startOfMonthUTC = (value: Date): Date => {
    const month = value.getUTCMonth() + 1
    const monthStr = month < 10 ? `0${month}` : `${month}`
    return new Date(`${value.getUTCFullYear()}-${monthStr}-01T00:00:00.000Z`)
}

export function endOfMonthUTC(d: Date): Date {
    const endDate = new Date(d)
    endDate.setUTCDate(1)
    endDate.setUTCMonth(endDate.getUTCMonth() + 1)
    endDate.setUTCDate(0)
    endDate.setUTCHours(23, 59, 59, 999)
    return endDate
}

export function addMonthUTC(d: Date, n: number = 1) {
    const addedMonth = new Date(d)
    const date = addedMonth.getUTCDate()
    addedMonth.setUTCDate(1)
    const nextMonth = addedMonth.getUTCMonth() + n
    addedMonth.setUTCMonth(nextMonth)
    addedMonth.setUTCDate(Math.min(date, endOfMonthUTC(addedMonth).getUTCDate()))
    return addedMonth
}

export const dateToYearMonthDayString = (d: Date): string => {
    return d.toISOString().substring(0, 10)
}

export const dateToYearMonthString = (d: Date): string => {
    return d.toISOString().substring(0, 7)
}

export const dateToYearMonthDayMaxNowString = (d: Date): string => {
    d = d.getTime() > Date.now() ? new Date() : d
    return dateToYearMonthDayString(d)
}

export const startOfDayUTC = (d: Date): Date => {
    return new Date(dateToYearMonthDayString(d))
}

export const endOfDayUTC = (d: Date): Date => {
    return new Date(`${dateToYearMonthDayString(d)}T23:59:59.999Z`)
}

export const endOfDayMaxNowUTC = (d: Date): Date => {
    const endOfDay = endOfDayUTC(d)
    return endOfDay.getTime() > Date.now() ? new Date() : endOfDay
}

export function prettyFormatError(e: any): string | undefined {
    const str = e?.toString()
    if (str === '[object Object]') {
        return tryStringify(e)
    }
    return str
}

export function isIpcPortMode(portMode: InputPort['mode']): portMode is IpcPortMode {
    return portMode in IpcPortMode
}

export function isInternalDerivedInputPortMode(portMode: InputPort['mode']): portMode is InternalDerivedInputPortMode {
    return portMode in InternalDerivedInputPortMode
}

export const DATE_FORMAT_SHORT = 'yyyy-MM-dd'
export const DATE_FORMAT_LONG = 'yyyy-MM-dd HH:mm:ss'
export const DATE_FORMAT_LONGER = 'yyyy-MM-dd HH:mm:ss.SSS'

export function omit<T extends Record<string, any>, K extends keyof T>(t: T, ...omitKeys: K[]) {
    const o = { ...t }
    for (const k of omitKeys) {
        delete o[k]
    }
    return o as Omit<T, K>
}

export const roleLevels: readonly Role[] = Object.freeze([Role.basic, Role.admin, Role.super])

export function getRoleLevel(role: Role) {
    const index = roleLevels.findIndex((r) => r === role)
    if (index === -1) {
        throw new Error(`Invalid role: ${role}`)
    }
    return index
}

export function maxRole(...roles: Role[]) {
    if (roles.length === 0) {
        throw new Error(`maxRole expects at least one role as input`)
    }
    let maxRole: Role = roles[0]
    for (const role of roles) {
        if (getRoleLevel(role) > getRoleLevel(maxRole)) {
            maxRole = role
        }
    }
    return maxRole
}

export function applyCodecRestrictions<
    T extends SupportedVideoCodec,
    Prop extends Exclude<ArrayKeys<T>, 'restrictions'>,
>(
    options: T[Prop],
    property: Prop,
    encoderSettings: GeneralEncoderSettings,
    restrictions?: Restrictions<T>
): NonNullable<T[Prop]> {
    // Yes, the typing here could be improved
    const opts = options || ([] as any[])
    if (!Array.isArray(opts)) {
        return opts as unknown as NonNullable<T[Prop]>
    }
    const propRestrictions = restrictions && restrictions[property]
    if (!propRestrictions) {
        return opts as unknown as NonNullable<T[Prop]>
    }
    const determinant = propRestrictions.determinedBy
    if (!(determinant in encoderSettings)) {
        return opts as unknown as NonNullable<T[Prop]>
    }
    const determinantValue = encoderSettings[determinant as keyof typeof encoderSettings]
    const mappedValues = propRestrictions.mapping[determinantValue as keyof typeof propRestrictions.mapping]
    if (!Array.isArray(mappedValues)) {
        return opts as unknown as NonNullable<T[Prop]>
    }
    return opts.filter((o) => {
        return mappedValues.includes(isNameValueOption(o) ? o.value : o)
    }) as unknown as NonNullable<T[Prop]>
}

export function isNameValueOption(v: any): v is Option {
    return typeof v === 'object' && typeof v['name'] === 'string' && 'value' in v
}

export function formatOutputStreamContents({
    ports,
    tsInfo,
    channelIds,
}: {
    ports: Output['ports']
    tsInfo?: TransportStream[]
    channelIds?: number[]
}) {
    if (isMatroxPortMode(ports[0].mode)) {
        return formatFromMatroxOutputPort(ports[0] as MatroxSdiOutputPort)
    } else {
        return getFormattedTransportStreamContent(tsInfoForChannels(tsInfo, channelIds))
    }
}

export function productIdFromEdgeProduct(edgeProduct: EdgeProduct) {
    switch (edgeProduct) {
        case EdgeProduct.loci:
            return products.connectIt.id
        case EdgeProduct.edge:
            return products.nimbraEdge.id
    }
}

function formatFromMatroxOutputPort(port: MatroxSdiOutputPort) {
    return port.decoderSettings.outputSettings.resolution
}

// This is how the status is stored in the database and the order used for sorting
export const inputOperStatusValues: { [key in InputOperStatus]: number } = {
    allOk: 0,
    reducedRedundancy: 10,
    notConfigured: 20,
    metricsMissing: 30,
    tr101290Priority1Error: 40,
    transportError: 50,
    alarm: 60,
    inputError: 70,
}

// This is how the status is stored in the database and the order used for sorting
export const outputOperStatusValues: { [key in OutputOperStatus]: number } = {
    allOk: 0,
    reducedRedundancy: 10,
    notAcknowledged: 20,
    notConfigured: 30,
    metricsMissing: 40,
    inputError: 50,
    tr101290Priority1Error: 60,
    alarm: 70,
    outputError: 80,
}

export const mapInputOperStatusFromInt: Map<number, InputOperStatus> = new Map(
    Object.entries(inputOperStatusValues).map(([key, value]) => [value, key as InputOperStatus])
)

export const mapOutputOperStatusFromInt: Map<number, OutputOperStatus> = new Map(
    Object.entries(outputOperStatusValues).map(([key, value]) => [value, key as OutputOperStatus])
)

export function inputOperStatusToInt(status: InputOperStatus): number {
    return inputOperStatusValues[status] ?? 80
}

export function inputOperStatusFromInt(status: number): InputOperStatus {
    return mapInputOperStatusFromInt.get(status) ?? InputOperStatus.inputError
}

export function outputOperStatusToInt(status: OutputOperStatus): number {
    return outputOperStatusValues[status] ?? 80
}

export function outputOperStatusFromInt(status: number): OutputOperStatus {
    return mapOutputOperStatusFromInt.get(status) ?? OutputOperStatus.outputError
}

// Infers the actually applied output redundancy given the received preferred output redundancy.
export function inferActualOutputRedundancy({
    ports,
    preferredRedundancyMode = OutputRedundancyMode.none,
}: {
    ports: Pick<OutputPort, 'copies'>[]
    preferredRedundancyMode: OutputRedundancyMode | undefined
}) {
    if (preferredRedundancyMode === OutputRedundancyMode.none) {
        // If the user preferred OutputRedundancyMode.none it will always be granted.
        return OutputRedundancyMode.none
    }
    // However, if the user preferred failover | active there are no guarantees,
    // e.g. due to the input not being redundant, the output being on a core node, etc.
    // Infer the actually applied redundancy based on the copies.
    const isRedundant = max([1, ...ports.map((p) => p.copies || 1)]) > 1
    return isRedundant ? preferredRedundancyMode : OutputRedundancyMode.none
}

/**
 * String columns in the telegraf stream tables that represent a discrete stream status; the in-app metric
 * graphs render these as status timelines. Shared between the metrics-series backend and the UI.
 */
export const METRICS_STATUS_COLUMN_NAMES = ['status', 'connectionStatus', 'state', 'multipathState', 'error'] as const

/**
 * ClickHouse telegraf table for a stream measurement type — identity for most types, while SDI/ASI
 * measurements are stored in the edge* tables.
 */
export function metricTypeToClickhouseTable(type: string): string {
    switch (type) {
        case 'sdiInput':
            return 'edgeSdiInput'
        case 'sdiOutput':
            return 'edgeSdiOutput'
        case 'asiInput':
            return 'edgeAsiInput'
        case 'asiOutput':
            return 'edgeAsiOutput'
        default:
            return type
    }
}
